import React from "react";

import { Card, Col, Form, Row } from "react-bootstrap";

import ContactFormReferenceSection from "@/components/contact/elements/ContactFormReference.component";
import {
  investmentFocusOptions,
  investmentRangeOptions,
} from "@/context/ContactProvider.context";
import { getCurrentLang } from "@/utils/Local";

import useContactContext from "../../../hooks/useContactContext";
import i18n from "../../../i18n";
import ContactFormLocationSection from "../elements/ContactFormLocationSection.component";
import ContactFormPhoneSection from "../elements/ContactFormPhoneSection.component";

const ContactFormInvestors = () => {
  const { t } = i18n;
  const { onSubmit, methods, submitting } = useContactContext();
  const investmentFocus = methods.watch("investmentFocus");

  return (
    <Form className="w-100" onSubmit={methods.handleSubmit(onSubmit)}>
      <fieldset disabled={submitting}>
        <input
          type="hidden"
          {...methods.register("kind")}
          label={null}
          value="investors"
        />
        <input
          type="hidden"
          {...methods.register("lang")}
          label={null}
          value={getCurrentLang()}
        />
        <Card>
          <Card.Body>
            <Row>
              <Col xs={12}>
                <Card.Title>{t("Primary Information")}</Card.Title>
              </Col>

              <Col xs={12}>
                <Row>
                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>{t("Full name")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...methods.register("name")}
                        isInvalid={!!methods.formState.errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.name?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="company">
                      <Form.Label>{t("Organization / Company")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...methods.register("company")}
                        isInvalid={!!methods.formState.errors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.company?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>{t("Email")}</Form.Label>
                      <Form.Control
                        type="email"
                        {...methods.register("email")}
                        isInvalid={!!methods.formState.errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.email?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <ContactFormPhoneSection
                      control={methods.control}
                      errors={methods.formState.errors}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Body>
            <ContactFormLocationSection methods={methods} />
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Body>
            <Row>
              <Col xs={12}>
                <Card.Title>{t("Investment Information")}</Card.Title>
              </Col>

              <Col lg={12} xs={12}>
                <Form.Group className="mb-3" controlId="investment-focus">
                  <Form.Label>{t("Investment Focus")}</Form.Label>
                  <div className="d-flex flex-wrap flex-fill gap-2 align-content-stretch">
                    {investmentFocusOptions.map((v) => (
                      <Form.Check
                        key={`key-${v}`}
                        className="checkbox-pill"
                        id={`investment-focus-${v}`}
                        label={t(v)}
                        type="checkbox"
                        value={v}
                        {...methods.register("investmentFocus", {
                          onChange: () => methods.trigger("investmentFocus"),
                        })}
                        isInvalid={!!methods.formState.errors.investmentFocus}
                        name="investmentFocus"
                      />
                    ))}
                  </div>
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {t(methods.formState.errors.investmentFocus?.message)}
                  </Form.Control.Feedback>
                </Form.Group>

                {investmentFocus &&
                (investmentFocus as typeof investmentFocusOptions)?.includes(
                  "Other",
                ) ? (
                  <Form.Group
                    className="mb-3"
                    controlId="investment-focus-other"
                  >
                    <Form.Control
                      placeholder="Describe 'Other'..."
                      type="text"
                      {...methods.register("investmentFocusOther")}
                      isInvalid={
                        !!methods.formState.errors.investmentFocusOther
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {t(
                        methods.formState.errors.investmentFocusOther?.message,
                      )}
                    </Form.Control.Feedback>
                  </Form.Group>
                ) : null}
              </Col>

              <Col lg={12} xs={12}>
                <Form.Group className="mb-3" controlId="investment-range">
                  <Form.Label>{t("Typical Investment Range")}</Form.Label>
                  <Form.Select
                    {...methods.register("investmentRange")}
                    isInvalid={!!methods.formState.errors.investmentRange}
                  >
                    <option value="">
                      -- {t("Please select an option")} --
                    </option>
                    {investmentRangeOptions.map((opt) => (
                      <option key={`r-${opt}`} value={opt}>
                        {t(opt)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {t(methods.formState.errors.investmentRange?.message)}
                  </Form.Control.Feedback>
                </Form.Group>

                <Col xs={12}>
                  <Form.Group className="mb-3" controlId="note">
                    <Form.Label>
                      {t(
                        "Tell us more about your interest (ex: residential house, cottage, etc.)",
                      )}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      type="text"
                      {...methods.register("note")}
                      isInvalid={!!methods.formState.errors.note}
                      placeholder={t(
                        "Please share your investment goals or areas of interest",
                      )}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t(methods.formState.errors.note?.message)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <ContactFormReferenceSection methods={methods} />
      </fieldset>
    </Form>
  );
};

export default ContactFormInvestors;
